import { Component, inject, OnInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Check } from '@app/core/model/entities/asset/check';
import { CheckType } from '@app/core/model/entities/asset/check-type';
import { IRelatedSpace, RelatedSpace } from '@app/core/model/entities/asset/space';
import { isNullOrEmpty } from '@app/shared/extra/utils';
import { CheckTypeService } from '@app/shared/services/check-type.service';
import { TranslateService } from '@ngx-translate/core';
import { FieldService } from '@services/field.service';
import { AccessManager } from '@services/managers/access.manager';
import { from, Observable } from 'rxjs';
import { filter, map, mergeMap, startWith, toArray } from 'rxjs/operators';

@Component({
    templateUrl: './organization-check-renew-modal.component.html',
    styleUrl: '../../../../../../shared/components/form-builder/sections/create-modal.component.scss',
    standalone: false
})
export class OrganizationCheckRenewModalComponent implements OnInit {
  private translateService = inject(TranslateService);
  private fb = inject(FormBuilder);
  private checkTypeService = inject(CheckTypeService);
  private fieldService = inject(FieldService);
  private accessManager = inject(AccessManager);
  private translate = inject(TranslateService);

  public checkCharacteristicsForm: FormGroup<{
    type: FormControl<string>,
    space: FormControl<string>
  }>;
  protected missingRequiredFields$: Observable<{ code: string, label: string }[]>;

  public checkToRenew: Check;
  public checkToRenewSpaces: RelatedSpace[];
  public checkToRenewType: CheckType;

  protected assetTypeAllowsSpaceModule = false;

  private destroyRef = inject(DestroyRef);

  constructor() {
    const data = inject<{ checkToRenew: Check }>(MAT_DIALOG_DATA);
    this.checkToRenew = data.checkToRenew;
    this.assetTypeAllowsSpaceModule = this.checkToRenew.relatedAssets.first().typeModules.includes(EntityTypeEnum.SPACE);
    this.checkToRenewSpaces = this.assetTypeAllowsSpaceModule ? this.checkToRenew.computedProperties.spaces
      .map((space: IRelatedSpace) => new RelatedSpace(space)) : [];

    // All fields with a value in the check to renew
    const checkExistingFields = Object.entries(this.checkToRenew)
      .filter(([_, value]) => !isNullOrEmpty(value))
      .map(([key]) => key)
      .concat(Object.keys(this.checkToRenew.properties));

    this.missingRequiredFields$ = this.fieldService.getFields('ALL', EntityTypeEnum.CHECK).pipe(
      mergeMap(fields => from(fields)),
      filter(field => (!field.computed || field.hasAlias) && checkExistingFields.includes(field.alias)),
      // Add check type since it's always required and config immutable
      startWith({code: 'checkType', alias: 'checkType', label: this.translate.instant('LABEL.CHECK_TYPE') as string}),
      // Keep remaining fields without sufficient user permissions
      filter(({alias}) => !this.accessManager.hasFieldWriteAccess(alias, EntityTypeEnum.CHECK)),
      map(({code, label}) => ({code, label})),
      toArray(),
      takeUntilDestroyed()
    );
  }

  /**
   * Load Check type and init form.
   */
  public ngOnInit(): void {
    this.checkTypeService.getCheckType(this.checkToRenew.type)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(checkType => this.checkToRenewType = checkType);

    this.checkCharacteristicsForm = this.fb.group({
      type: [{
        value: this.translateService.instant('VALUE.' + this.checkToRenew.type.toUpperCase()),
        disabled: true
      }],
      space: [{
        value: this.checkToRenewSpaces.toSorted().join(', '),
        disabled: true
      }]
    });
  }

  /**
   * Get the filled data for renewing a Check.
   * @return Check's data.
   */
  public getGeneratedObject(): { spaceIds: string[] } {
    return {
      spaceIds: this.checkToRenew.spaceIds
    };
  }
}
